import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminAPI, fileAPI } from '../lib/api';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import api from '../lib/api';
import { getApiUrl } from '../config/api.config';
import CreateUserForm from '../components/admin/CreateUserForm';
import PermissionsTab from '../components/admin/PermissionsTab';
import FileUpload from '../components/FileUpload';
import FilePreviewModal from '../components/FilePreviewModal';
import PitchEventList from './PitchEventList';
import SettingsPage from './SettingsPage';
import InvestorsManagement from './InvestorsManagement';
import CreateStartupModal from '../components/CreateStartupModal';
import AlertDialog from '../components/AlertDialog';
import ConfirmDialog from '../components/ConfirmDialog';
import FileTraceModal from '../components/FileTraceModal';

const AdminDashboard = () => {
	const { user, logout } = useAuth();
	const navigate = useNavigate();
	const location = useLocation();
	const { tenantId } = useParams();

	// Determine active tab from URL
	const getTabFromPath = () => {
		const path = location.pathname;
		if (path.includes('/files')) return 'files';
		if (path.includes('/startups')) return 'startups';
		if (path.includes('/investors')) return 'investors';
		if (path.includes('/users')) return 'users';
		if (path.includes('/reports')) return 'reports';
		if (path.includes('/events')) return 'events';
		if (path.includes('/settings')) return 'settings';
		if (path.includes('/account')) return 'account';
		return 'dashboard'; // default
	};

	const [activeTab, setActiveTab] = useState(getTabFromPath());
	const [users, setUsers] = useState([]);
	const [startups, setStartups] = useState([]);
	const [investors, setInvestors] = useState([]);
	const [investorNetworks, setInvestorNetworks] = useState([]);
	const [files, setFiles] = useState([]);
	const [selectedFile, setSelectedFile] = useState(null);
	const [loading, setLoading] = useState(false);
	const [showPermissions, setShowPermissions] = useState(false);
	const [showPreview, setShowPreview] = useState(false);
	const [fileTab, setFileTab] = useState('received');
	const [previewFile, setPreviewFile] = useState(null);
	const [showMobileMenu, setShowMobileMenu] = useState(false);
	const [showUserMenu, setShowUserMenu] = useState(false);
	const userMenuRef = useRef(null);
	const [showCreateStartupModal, setShowCreateStartupModal] = useState(false);
	const [startupsCurrentPage, setStartupsCurrentPage] = useState(1);
	const startupsPerPage = 10;
	const [alert, setAlert] = useState({ isOpen: false, title: '', message: '', type: 'info' });
	const [confirm, setConfirm] = useState({ isOpen: false, title: '', message: '', onConfirm: null, type: 'warning' });
	const [selectedUser, setSelectedUser] = useState(null);
	// Users pagination and search
	const [usersCurrentPage, setUsersCurrentPage] = useState(1);
	const [usersSearchTerm, setUsersSearchTerm] = useState('');
	const usersPerPage = 10;
	const [traceFile, setTraceFile] = useState(null);
	const [showTraceModal, setShowTraceModal] = useState(false);

	// Reports data state
	const [reportsLoading, setReportsLoading] = useState(false);
	const [dashboardData, setDashboardData] = useState(null);
	const [investmentData, setInvestmentData] = useState(null);
	const [pitchEventData, setPitchEventData] = useState(null);
	const [userActivityData, setUserActivityData] = useState(null);
	const [reportsActiveTab, setReportsActiveTab] = useState('dashboard');
	const COLORS = useMemo(() => ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4'], []);

	// Filter users based on search term
	const filteredUsers = useMemo(() => {
		if (!usersSearchTerm.trim()) return users;
		const searchLower = usersSearchTerm.toLowerCase();
		return users.filter(user =>
			(user.name?.toLowerCase().includes(searchLower) || '') ||
			(user.email?.toLowerCase().includes(searchLower) || '') ||
			(user.role?.toLowerCase().includes(searchLower) || '')
		);
	}, [users, usersSearchTerm]);

	// Calculate pagination for filtered users
	const usersTotalPages = Math.ceil(filteredUsers.length / usersPerPage);
	const usersStartIndex = (usersCurrentPage - 1) * usersPerPage;
	const usersEndIndex = usersStartIndex + usersPerPage;
	const currentUsers = filteredUsers.slice(usersStartIndex, usersEndIndex);

	// Close user menu when clicking outside
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
				setShowUserMenu(false);
			}
		};

		if (showUserMenu) {
			document.addEventListener('mousedown', handleClickOutside);
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [showUserMenu]);

	// Sync activeTab with URL changes
	useEffect(() => {
		setActiveTab(getTabFromPath());
	}, [location.pathname]);

	useEffect(() => {
		loadData();
	}, []);

	// Load reports data when reports tab is active
	useEffect(() => {
		if (activeTab === 'reports') {
			loadAllReports();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeTab]);

	const loadData = async () => {
		setLoading(true);
		try {
			const [usersData, startupsData, investorsData, networksData, filesData] = await Promise.all([
				adminAPI.getAllUsers(),
				adminAPI.getStartups(),
				adminAPI.getInvestors(),
				fetch(getApiUrl('admin/users/investor-networks'), {
					headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
				}).then(r => r.json()),
				adminAPI.getAllFiles()
			]);
			setUsers(usersData);
			setStartups(startupsData);
			setInvestors(investorsData);
			setInvestorNetworks(networksData);
			// Sort files by date (most recent first)
			const sortedFiles = [...filesData].sort((a, b) => {
				const dateA = new Date(a.uploadedAt || a.createdAt || 0);
				const dateB = new Date(b.uploadedAt || b.createdAt || 0);
				return dateB - dateA; // Descending order (newest first)
			});
			setFiles(sortedFiles);
		} catch (err) {
			console.error('Failed to load data:', err);
		} finally {
			setLoading(false);
		}
	};

	const loadAllReports = useCallback(async () => {
		setReportsLoading(true);
		try {
			const [dashboard, investments, pitchEvents, userActivity] = await Promise.all([
				api.get('/reports/dashboard').catch(err => {
					console.error('Error loading dashboard report:', err);
					return { data: null };
				}),
				api.get('/reports/investments').catch(err => {
					console.error('Error loading investment report:', err);
					return { data: null };
				}),
				api.get('/reports/pitch-events').catch(err => {
					console.error('Error loading pitch event report:', err);
					return { data: null };
				}),
				api.get('/reports/user-activity?days=30').catch(err => {
					console.error('Error loading user activity report:', err);
					return { data: null };
				})
			]);

			setDashboardData(dashboard.data);
			setInvestmentData(investments.data);
			setPitchEventData(pitchEvents.data);
			setUserActivityData(userActivity.data);
		} catch (error) {
			console.error('Error loading reports:', error);
		} finally {
			setReportsLoading(false);
		}
	}, []);

	const handleLogout = () => {
		logout();
		navigate('/login');
	};

	// Helper functions for reports
	const formatCurrency = (amount) => {
		if (!amount) return '₹0';
		return new Intl.NumberFormat('en-IN', {
			style: 'currency',
			currency: 'INR',
			minimumFractionDigits: 0,
			maximumFractionDigits: 0
		}).format(amount);
	};

	const KPICard = ({ title, value, icon, color }) => {
		const colorClasses = {
			blue: 'bg-blue-100 text-blue-600',
			green: 'bg-green-100 text-green-600',
			purple: 'bg-purple-100 text-purple-600',
			yellow: 'bg-yellow-100 text-yellow-600',
			red: 'bg-red-100 text-red-600',
			orange: 'bg-orange-100 text-orange-600'
		};

		return (
			<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
				<div className="flex items-center">
					<div className="flex-shrink-0">
						<div className={`w-12 h-12 ${colorClasses[color] || colorClasses.blue} rounded-lg flex items-center justify-center`}>
							<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={icon} />
							</svg>
						</div>
					</div>
					<div className="ml-4">
						<p className="text-sm font-medium text-gray-600">{title}</p>
						<p className="text-2xl font-bold text-gray-900">{value}</p>
					</div>
				</div>
			</div>
		);
	};

	// Reports tab definitions
	const reportsTabs = useMemo(() => [
		{ id: 'dashboard', label: 'Dashboard' },
		{ id: 'investments', label: 'Investments' },
		{ id: 'pitch-events', label: 'Pitch Events' },
		{ id: 'user-activity', label: 'User Activity' }
	], []);

	// Reports render functions
	const renderReportsDashboardTab = () => (
		<div className="space-y-6">
			<h2 className="text-2xl font-semibold text-gray-900">Dashboard Overview</h2>

			{/* KPI Cards */}
			<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
				<KPICard
					title="Total Users"
					value={dashboardData?.totalUsers || 0}
					icon="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
					color="blue"
				/>
				<KPICard
					title="Active Startups"
					value={dashboardData?.activeStartups || 0}
					icon="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
					color="green"
				/>
				<KPICard
					title="Total Investments"
					value={dashboardData?.totalInvestments || 0}
					icon="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
					color="purple"
				/>
				<KPICard
					title="Total Invested"
					value={formatCurrency(dashboardData?.totalInvestedAmount || 0)}
					icon="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
					color="yellow"
				/>
			</div>

			{/* Additional Stats */}
			<div className="grid grid-cols-1 md:grid-cols-3 gap-6">
				<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
					<h3 className="text-lg font-semibold text-gray-900 mb-4">Pitch Events</h3>
					<div className="space-y-2">
						<div className="flex justify-between">
							<span className="text-gray-600">Total Events</span>
							<span className="font-semibold">{dashboardData?.totalPitchEvents || 0}</span>
						</div>
						<div className="flex justify-between">
							<span className="text-gray-600">Upcoming</span>
							<span className="font-semibold text-blue-600">{dashboardData?.upcomingEvents || 0}</span>
						</div>
						<div className="flex justify-between">
							<span className="text-gray-600">Completed</span>
							<span className="font-semibold text-green-600">{dashboardData?.completedEvents || 0}</span>
						</div>
					</div>
				</div>

				<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
					<h3 className="text-lg font-semibold text-gray-900 mb-4">Investor Network</h3>
					<div className="space-y-2">
						<div className="flex justify-between">
							<span className="text-gray-600">Investor Networks</span>
							<span className="font-semibold">{dashboardData?.investorNetworks || 0}</span>
						</div>
						<div className="flex justify-between">
							<span className="text-gray-600">Active Investors</span>
							<span className="font-semibold">{dashboardData?.activeInvestors || 0}</span>
						</div>
					</div>
				</div>

				<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
					<h3 className="text-lg font-semibold text-gray-900 mb-4">Files</h3>
					<div className="space-y-2">
						<div className="flex justify-between">
							<span className="text-gray-600">Total Files</span>
							<span className="font-semibold">{dashboardData?.totalFiles || 0}</span>
						</div>
					</div>
				</div>
			</div>
		</div>
	);

	const renderReportsInvestmentsTab = () => (
		<div className="space-y-6">
			<div className="flex justify-between items-center">
				<h2 className="text-2xl font-semibold text-gray-900">Investment Analytics</h2>
				<div className="text-sm text-gray-500">
					Total Invested: <span className="font-semibold text-gray-900">{formatCurrency(investmentData?.totalAmount || 0)}</span>
					<span className="mx-2">•</span>
					{investmentData?.totalInvestments || 0} Investments
				</div>
			</div>

			{/* Investment by Stage Chart */}
			{investmentData?.investmentsByStage && Object.keys(investmentData.investmentsByStage).length > 0 ? (
				<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
					<h3 className="text-lg font-semibold text-gray-900 mb-4">Investments by Stage</h3>
					<ResponsiveContainer width="100%" height={300}>
						<BarChart data={Object.entries(investmentData.investmentsByStage).map(([key, value]) => ({
							stage: key,
							count: value?.count || 0,
							amount: value?.totalAmount || 0
						}))}>
							<CartesianGrid strokeDasharray="3 3" />
							<XAxis dataKey="stage" />
							<YAxis yAxisId="left" />
							<YAxis yAxisId="right" orientation="right" />
							<Tooltip />
							<Legend />
							<Bar yAxisId="left" dataKey="count" fill="#3B82F6" name="Count" />
							<Bar yAxisId="right" dataKey="amount" fill="#10B981" name="Amount (INR)" />
						</BarChart>
					</ResponsiveContainer>
				</div>
			) : (
				<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
					<h3 className="text-lg font-semibold text-gray-900 mb-4">Investments by Stage</h3>
					<div className="text-center py-8 text-gray-500">No investment data available by stage</div>
				</div>
			)}

			{/* Top Investors */}
			{investmentData?.topInvestors && investmentData.topInvestors.length > 0 ? (
				<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
					<h3 className="text-lg font-semibold text-gray-900 mb-4">Top Investors by Amount</h3>
					<ResponsiveContainer width="100%" height={300}>
						<BarChart data={investmentData.topInvestors.map(inv => ({
							name: inv.investorName,
							amount: inv.totalAmount,
							count: inv.investmentCount
						}))}>
							<CartesianGrid strokeDasharray="3 3" />
							<XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
							<YAxis />
							<Tooltip formatter={(value) => formatCurrency(value)} />
							<Bar dataKey="amount" fill="#8B5CF6" name="Total Invested" />
						</BarChart>
					</ResponsiveContainer>
				</div>
			) : (
				<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
					<h3 className="text-lg font-semibold text-gray-900 mb-4">Top Investors by Amount</h3>
					<div className="text-center py-8 text-gray-500">No investor data available</div>
				</div>
			)}

			{/* Top Startups */}
			{investmentData?.topStartups && investmentData.topStartups.length > 0 ? (
				<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
					<h3 className="text-lg font-semibold text-gray-900 mb-4">Top Funded Startups</h3>
					<div className="overflow-x-auto">
						<table className="min-w-full divide-y divide-gray-200">
							<thead className="bg-gray-50">
								<tr>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Startup</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Investments</th>
									<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Raised</th>
								</tr>
							</thead>
							<tbody className="bg-white divide-y divide-gray-200">
								{investmentData.topStartups.map((startup, idx) => (
									<tr key={idx}>
										<td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">{startup.startupName}</td>
										<td className="px-6 py-4 whitespace-nowrap text-gray-500">{startup.investmentCount}</td>
										<td className="px-6 py-4 whitespace-nowrap text-gray-900">{formatCurrency(startup.totalAmount)}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>
			) : (
				<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
					<h3 className="text-lg font-semibold text-gray-900 mb-4">Top Funded Startups</h3>
					<div className="text-center py-8 text-gray-500">No startup investment data available</div>
				</div>
			)}
		</div>
	);

	const renderReportsPitchEventsTab = () => (
		<div className="space-y-6">
			<div className="flex justify-between items-center">
				<h2 className="text-2xl font-semibold text-gray-900">Pitch Event Reports</h2>
				<div className="text-sm text-gray-500">
					Total Events: <span className="font-semibold text-gray-900">{pitchEventData?.totalEvents || 0}</span>
				</div>
			</div>

			{/* Events by Status */}
			{pitchEventData?.byStatus && (
				<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
					<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
						<h3 className="text-lg font-semibold text-gray-900 mb-4">Events by Status</h3>
						<ResponsiveContainer width="100%" height={250}>
							<PieChart>
								<Pie
									data={Object.entries(pitchEventData.byStatus).map(([name, value]) => ({ name, value }))}
									cx="50%"
									cy="50%"
									labelLine={false}
									label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
									outerRadius={80}
									fill="#8884d8"
									dataKey="value"
								>
									{Object.entries(pitchEventData.byStatus).map((entry, index) => (
										<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
									))}
								</Pie>
								<Tooltip />
							</PieChart>
						</ResponsiveContainer>
					</div>

					<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
						<h3 className="text-lg font-semibold text-gray-900 mb-4">Participation Stats</h3>
						<div className="space-y-4">
							<div>
								<div className="flex justify-between mb-2">
									<span className="text-gray-600">Total Participants</span>
									<span className="font-semibold">{pitchEventData?.totalParticipants || 0}</span>
								</div>
							</div>
							<div>
								<div className="flex justify-between mb-2">
									<span className="text-gray-600">Avg Participants/Event</span>
									<span className="font-semibold">{(pitchEventData?.avgParticipantsPerEvent || 0).toFixed(1)}</span>
								</div>
							</div>
						</div>
					</div>
				</div>
			)}

			{/* Events by Type */}
			{pitchEventData?.byType && Object.keys(pitchEventData.byType).length > 0 && (
				<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
					<h3 className="text-lg font-semibold text-gray-900 mb-4">Events by Type</h3>
					<ResponsiveContainer width="100%" height={250}>
						<BarChart data={Object.entries(pitchEventData.byType).map(([name, value]) => ({ name, value }))}>
							<CartesianGrid strokeDasharray="3 3" />
							<XAxis dataKey="name" />
							<YAxis />
							<Tooltip />
							<Bar dataKey="value" fill="#06B6D4" />
						</BarChart>
					</ResponsiveContainer>
				</div>
			)}

			{/* Upcoming Events */}
			{pitchEventData?.upcomingEvents && pitchEventData.upcomingEvents.length > 0 && (
				<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
					<h3 className="text-lg font-semibold text-gray-900 mb-4">Upcoming Events</h3>
					<div className="space-y-3">
						{pitchEventData.upcomingEvents.map(event => (
							<div key={event.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
								<div>
									<p className="font-medium text-gray-900">{event.eventName}</p>
									<p className="text-sm text-gray-500">
										{new Date(event.eventDate).toLocaleDateString()}
									</p>
								</div>
								<div className="text-right">
									<p className="text-sm font-medium text-gray-900">{event.participantCount} Participants</p>
								</div>
							</div>
						))}
					</div>
				</div>
			)}
		</div>
	);

	const renderReportsUserActivityTab = () => (
		<div className="space-y-6">
			<div className="flex justify-between items-center">
				<h2 className="text-2xl font-semibold text-gray-900">User Activity (Last {userActivityData?.periodDays || 30} Days)</h2>
			</div>

			{/* Activity Overview */}
			<div className="grid grid-cols-1 md:grid-cols-4 gap-6">
				<KPICard
					title="Total Access Attempts"
					value={userActivityData?.totalAccessAttempts || 0}
					icon="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
					color="blue"
				/>
				<KPICard
					title="Successful Access"
					value={userActivityData?.successfulAccess || 0}
					icon="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
					color="green"
				/>
				<KPICard
					title="Denied Access"
					value={userActivityData?.deniedAccess || 0}
					icon="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
					color="red"
				/>
				<KPICard
					title="Success Rate"
					value={`${userActivityData?.totalAccessAttempts ?
						((userActivityData.successfulAccess / userActivityData.totalAccessAttempts) * 100).toFixed(1) : 0}%`}
					icon="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z"
					color="purple"
				/>
			</div>

			{/* Access by Type */}
			{userActivityData?.byAccessType && (
				<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
					<h3 className="text-lg font-semibold text-gray-900 mb-4">Access by Type</h3>
					<ResponsiveContainer width="100%" height={250}>
						<BarChart data={Object.entries(userActivityData.byAccessType).map(([name, value]) => ({ name, value }))}>
							<CartesianGrid strokeDasharray="3 3" />
							<XAxis dataKey="name" />
							<YAxis />
							<Tooltip />
							<Bar dataKey="value" fill="#F59E0B" />
						</BarChart>
					</ResponsiveContainer>
				</div>
			)}

			{/* Most Active Users */}
			{userActivityData?.mostActiveUsers && userActivityData.mostActiveUsers.length > 0 && (
				<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
					<h3 className="text-lg font-semibold text-gray-900 mb-4">Most Active Users</h3>
					<div className="space-y-3">
						{userActivityData.mostActiveUsers.map((user, idx) => (
							<div key={idx} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
								<div className="flex items-center space-x-3">
									<div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
										<span className="text-blue-600 font-semibold text-sm">{idx + 1}</span>
									</div>
									<span className="font-medium text-gray-900">{user.userName}</span>
								</div>
								<span className="text-sm font-semibold text-gray-900">{user.activityCount} activities</span>
							</div>
						))}
					</div>
				</div>
			)}

			{/* Most Accessed Files */}
			{userActivityData?.mostAccessedFiles && userActivityData.mostAccessedFiles.length > 0 && (
				<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
					<h3 className="text-lg font-semibold text-gray-900 mb-4">Most Accessed Files</h3>
					<div className="space-y-3">
						{userActivityData.mostAccessedFiles.map((file, idx) => (
							<div key={idx} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
								<div className="flex items-center space-x-3">
									<div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
										<span className="text-green-600 font-semibold text-sm">{idx + 1}</span>
									</div>
									<span className="font-medium text-gray-900 truncate max-w-md">{file.fileName}</span>
								</div>
								<span className="text-sm font-semibold text-gray-900">{file.accessCount} times</span>
							</div>
						))}
					</div>
				</div>
			)}
		</div>
	);

	const openPermissions = (file) => {
		setSelectedFile(file);
		setShowPermissions(true);
	};

	const closePermissions = () => {
		setShowPermissions(false);
		setSelectedFile(null);
	};

	const openPreview = (file) => {
		setPreviewFile(file);
		setShowPreview(true);
	};

	const closePreview = () => {
		setPreviewFile(null);
		setShowPreview(false);
	};

	const handleDownloadFile = async (file) => {
		try {
			const response = await fileAPI.getFileDownload(file.id);
			const url = window.URL.createObjectURL(new Blob([response.data]));
			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', file.originalName || 'downloaded-file');
			document.body.appendChild(link);
			link.click();
			link.parentNode.removeChild(link);
		} catch (error) {
			console.error('Download error:', error);
			setAlert({ isOpen: true, title: 'Error', message: 'Failed to download file', type: 'error' });
		}
	};

	const deleteFile = (file) => {
		setConfirm({
			isOpen: true,
			title: 'Delete File',
			message: `Are you sure you want to delete "${file.originalName}"? This will permanently delete the file and remove it from all shared views.`,
			type: 'danger',
			onConfirm: async () => {
				await performDeleteFile(file.id);
			}
		});
	};

	const performDeleteFile = async (fileId) => {
		try {
			await fileAPI.deleteFile(fileId);
			setAlert({ isOpen: true, title: 'Success', message: 'File deleted successfully', type: 'success' });
			loadData();
		} catch (err) {
			console.error('Delete error:', err);
			setAlert({ isOpen: true, title: 'Error', message: err.message || 'Failed to delete file', type: 'error' });
		} finally {
			setConfirm({ ...confirm, isOpen: false });
		}
	};

	const handleDeleteUser = (user) => {
		setSelectedUser(user);
		setConfirm({
			isOpen: true,
			title: 'Delete User',
			message: `Are you sure you want to delete user "${user.name}" (${user.email})? This action cannot be undone.`,
			type: 'warning',
			onConfirm: async () => {
				await performDeleteUser(user.id);
			}
		});
	};

	const performDeleteUser = async (userId) => {
		try {
			const response = await fetch(getApiUrl(`admin/users/${userId}`), {
				method: 'DELETE',
				headers: {
					'Authorization': `Bearer ${localStorage.getItem('token')}`
				}
			});

			if (response.ok) {
				setAlert({
					isOpen: true,
					title: 'Success',
					message: 'User deleted successfully',
					type: 'success'
				});
				await loadData();
			} else {
				const errorData = await response.json().catch(() => ({ message: 'Failed to delete user' }));
				throw new Error(errorData.message || 'Failed to delete user');
			}
		} catch (error) {
			console.error('Failed to delete user:', error);
			setAlert({
				isOpen: true,
				title: 'Error',
				message: error.message || 'Failed to delete user',
				type: 'error'
			});
		} finally {
			setConfirm({ ...confirm, isOpen: false });
			setSelectedUser(null);
		}
	};

	const handleResetPassword = (user) => {
		setSelectedUser(user);
		setConfirm({
			isOpen: true,
			title: 'Reset Password',
			message: `Are you sure you want to reset the password for user "${user.name}" (${user.email})? A temporary password will be generated.`,
			type: 'info',
			onConfirm: async () => {
				await performResetPassword(user.id);
			}
		});
	};

	const performResetPassword = async (userId) => {
		try {
			const response = await fetch(getApiUrl(`admin/users/${userId}/reset-password`), {
				method: 'POST',
				headers: {
					'Authorization': `Bearer ${localStorage.getItem('token')}`
				}
			});

			if (response.ok) {
				const data = await response.json();
				const tempPassword = data.tempPassword;
				if (!tempPassword) {
					throw new Error('Temporary password was not returned by the server. Deploy the latest API or check logs.');
				}

				// Create a custom alert with copy functionality
				const copyToClipboard = async () => {
					try {
						await navigator.clipboard.writeText(tempPassword);
						// Show visual feedback
						const copyBtn = document.getElementById('copy-password-btn');
						if (copyBtn) {
							const originalText = copyBtn.innerHTML;
							copyBtn.innerHTML = `
								<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
								</svg>
								<span>Copied!</span>
							`;
							copyBtn.className = 'inline-flex items-center space-x-1 px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg';
							setTimeout(() => {
								copyBtn.innerHTML = originalText;
								copyBtn.className = 'inline-flex items-center space-x-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition';
							}, 2000);
						}
					} catch (err) {
						console.error('Failed to copy:', err);
					}
				};

				setAlert({
					isOpen: true,
					title: 'Password Reset Successfully',
					message: (
						<div className="space-y-3">
							<p className="text-sm text-gray-600">
								The temporary password has been generated. Please copy it and share it securely with the user.
							</p>
							<div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
								<div className="flex items-center justify-between">
									<code className="text-base font-mono font-semibold text-gray-900 select-all">
										{tempPassword}
									</code>
									<button
										id="copy-password-btn"
										onClick={copyToClipboard}
										className="inline-flex items-center space-x-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition"
									>
										<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
										</svg>
										<span>Copy</span>
									</button>
								</div>
							</div>
							<p className="text-xs text-gray-500">
								⚠️ This password will only be shown once. Make sure to copy it before closing this dialog.
							</p>
						</div>
					),
					type: 'success'
				});
			} else {
				const errorData = await response.json().catch(() => ({ message: 'Failed to reset password' }));
				throw new Error(errorData.message || 'Failed to reset password');
			}
		} catch (error) {
			console.error('Failed to reset password:', error);
			setAlert({
				isOpen: true,
				title: 'Error',
				message: error.message || 'Failed to reset password',
				type: 'error'
			});
		} finally {
			setConfirm({ ...confirm, isOpen: false });
			setSelectedUser(null);
		}
	};

	return (
		<>
			<div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-6">
				{showPermissions ? (
					<PermissionsTab
						file={selectedFile}
						investors={investors}
						investorNetworks={investorNetworks}
						users={users.filter(u => u.role === 'MANAGEMENT')}
						onBack={closePermissions}
					/>
				) : (
					<>
						{/* Content Area */}
						<div className="bg-white rounded-xl shadow-sm border border-gray-200">
							{loading ? (
								<div className="text-center py-16">
									<div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
										<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
									</div>
									<p className="text-lg font-medium text-gray-900">Loading data...</p>
									<p className="text-sm text-gray-500 mt-1">Please wait while we fetch the information</p>
								</div>
							) : activeTab === 'dashboard' ? (
								<div className="p-6 space-y-6">
									<h2 className="text-2xl font-semibold text-gray-900 mb-6">Dashboard Overview</h2>

									{/* KPI Cards */}
									<div className="grid grid-cols-1 md:grid-cols-4 gap-6">
										<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
											<div className="flex items-center">
												<div className="flex-shrink-0">
													<div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
														<svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
															<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
														</svg>
													</div>
												</div>
												<div className="ml-4">
													<p className="text-sm font-medium text-gray-600">Total Users</p>
													<p className="text-2xl font-bold text-gray-900">{users.length}</p>
												</div>
											</div>
										</div>
										<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
											<div className="flex items-center">
												<div className="flex-shrink-0">
													<div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
														<svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
															<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
														</svg>
													</div>
												</div>
												<div className="ml-4">
													<p className="text-sm font-medium text-gray-600">Files</p>
													<p className="text-2xl font-bold text-gray-900">{files.length}</p>
												</div>
											</div>
										</div>
										<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
											<div className="flex items-center">
												<div className="flex-shrink-0">
													<div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
														<svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
															<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
														</svg>
													</div>
												</div>
												<div className="ml-4">
													<p className="text-sm font-medium text-gray-600">Startups</p>
													<p className="text-2xl font-bold text-gray-900">{startups.length}</p>
												</div>
											</div>
										</div>
										<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
											<div className="flex items-center">
												<div className="flex-shrink-0">
													<div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
														<svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
															<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
														</svg>
													</div>
												</div>
												<div className="ml-4">
													<p className="text-sm font-medium text-gray-600">Investors</p>
													<p className="text-2xl font-bold text-gray-900">{investors.length}</p>
												</div>
											</div>
										</div>
									</div>

									{/* Quick Actions */}
									<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
										<h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
										<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
											<button
												onClick={() => navigate(`/${tenantId}/files`)}
												className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition"
											>
												<svg className="w-8 h-8 text-blue-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
												</svg>
												<div className="text-left">
													<p className="font-medium text-gray-900">Manage Files</p>
													<p className="text-sm text-gray-500">Upload and organize files</p>
												</div>
											</button>
											<button
												onClick={() => navigate(`/${tenantId}/users`)}
												className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition"
											>
												<svg className="w-8 h-8 text-green-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
												</svg>
												<div className="text-left">
													<p className="font-medium text-gray-900">Manage Users</p>
													<p className="text-sm text-gray-500">Add and edit users</p>
												</div>
											</button>
											<button
												onClick={() => navigate(`/${tenantId}/reports`)}
												className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition"
											>
												<svg className="w-8 h-8 text-purple-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
												</svg>
												<div className="text-left">
													<p className="font-medium text-gray-900">View Reports</p>
													<p className="text-sm text-gray-500">Analytics and insights</p>
												</div>
											</button>
										</div>
									</div>
								</div>
							) : activeTab === 'files' ? (
								<div className="p-6 space-y-6">
									<div>
										<h2 className="text-xl font-semibold text-gray-900">File Management</h2>
										<p className="text-sm text-gray-500 mt-1">Manage uploaded files and permissions</p>
									</div>

									{/* File Upload Section */}
									<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
										<FileUpload
											onUploadSuccess={(fileName) => {
												console.log('File uploaded successfully:', fileName);
												loadData(); // Refresh the files list
											}}
											onUploadError={(error) => {
												console.error('Upload error:', error);
												// Note: FileUpload component should handle its own error display
											}}
										/>
									</div>

									{/* File Tabs */}
									<div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
										<div className="border-b border-gray-200">
											<nav className="flex -mb-px px-6" aria-label="Tabs">
												<button
													onClick={() => setFileTab('received')}
													className={`${fileTab === 'received'
														? 'border-blue-500 text-blue-600'
														: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
														} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm mr-8`}
												>
													Received Files
												</button>
												<button
													onClick={() => setFileTab('sent')}
													className={`${fileTab === 'sent'
														? 'border-blue-500 text-blue-600'
														: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
														} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
												>
													Sent Files
												</button>
											</nav>
										</div>

										{/* Files Table with Scrollable Container */}
										<div className="max-h-[calc(100vh-460px)] overflow-y-auto">
											<table className="min-w-full divide-y divide-gray-300">
												<thead className="bg-gray-50 sticky top-0 z-10">
													<tr>
														<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
															File
														</th>
														<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
															Document Type
														</th>
														<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
															Uploaded By
														</th>
														<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
															Date
														</th>
														<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
															Actions
														</th>
													</tr>
												</thead>
												<tbody className="bg-white divide-y divide-gray-200">
													{files
														.filter(file => {
															if (fileTab === 'sent') {
																return file.uploadedById === user?.id;
															} else {
																return file.uploadedById !== user?.id;
															}
														})
														.map((file) => (
															<tr key={file.id} className="hover:bg-gray-50">
																<td className="px-6 py-4 whitespace-nowrap">
																	<div className="flex items-center">
																		<div className="flex-shrink-0 h-10 w-10">
																			<div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
																				<svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																					<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
																				</svg>
																			</div>
																		</div>
																		<div className="ml-4">
																			<button
																				onClick={() => openPreview(file)}
																				className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
																			>
																				{file.originalName}
																			</button>
																			<div className="text-sm text-gray-500">{file.fileSize ? `${(file.fileSize / 1024 / 1024).toFixed(2)} MB` : 'Unknown size'}</div>
																		</div>
																	</div>
																</td>
																<td className="px-6 py-4 whitespace-nowrap">
																	{file.fileCategory ? (
																		<span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${file.fileCategory.includes('PITCH') || file.fileCategory.includes('PRESENTATION') || file.fileCategory.includes('DECK') ? 'bg-purple-100 text-purple-800' :
																			file.fileCategory.includes('FINANCIAL') || file.fileCategory.includes('VALUATION') || file.fileCategory.includes('CAP_TABLE') || file.fileCategory.includes('BALANCE') || file.fileCategory.includes('CASH_FLOW') || file.fileCategory.includes('P_L') ? 'bg-green-100 text-green-800' :
																				file.fileCategory.includes('LEGAL') || file.fileCategory.includes('AGREEMENT') || file.fileCategory.includes('SHA') || file.fileCategory.includes('TERM') || file.fileCategory.includes('NDA') || file.fileCategory.includes('PATENT') || file.fileCategory.includes('TRADEMARK') || file.fileCategory.includes('IP') ? 'bg-blue-100 text-blue-800' :
																					file.fileCategory.includes('BUSINESS') || file.fileCategory.includes('MARKET') || file.fileCategory.includes('COMPETITIVE') || file.fileCategory.includes('PRODUCT') || file.fileCategory.includes('TECHNICAL') ? 'bg-orange-100 text-orange-800' :
																						file.fileCategory.includes('COMPLIANCE') || file.fileCategory.includes('LICENSE') || file.fileCategory.includes('REGULATORY') || file.fileCategory.includes('TAX') ? 'bg-yellow-100 text-yellow-800' :
																							file.fileCategory.includes('OPERATIONAL') || file.fileCategory.includes('HR') || file.fileCategory.includes('POLICY') || file.fileCategory.includes('PROCESS') ? 'bg-indigo-100 text-indigo-800' :
																								file.fileCategory.includes('MARKETING') || file.fileCategory.includes('SALES') || file.fileCategory.includes('BRAND') ? 'bg-pink-100 text-pink-800' :
																									'bg-gray-100 text-gray-800'
																			}`}>
																			{file.fileCategory.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
																		</span>
																	) : (
																		<span className="text-xs text-gray-400">General</span>
																	)}
																</td>
																<td className="px-6 py-4 whitespace-nowrap">
																	<div className="flex items-center">
																		<div className="flex-shrink-0 h-8 w-8">
																			<div className="h-8 w-8 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 flex items-center justify-center">
																				<span className="text-white text-xs font-medium">
																					{(file.uploadedByName || file.uploadedBy || 'Unknown')?.charAt(0)?.toUpperCase()}
																				</span>
																			</div>
																		</div>
																		<div className="ml-3">
																			<div className="text-sm font-medium text-gray-900">{file.uploadedByName || file.uploadedBy || 'Unknown User'}</div>
																			{file.uploadedByEmail && (
																				<div className="text-xs text-gray-500">{file.uploadedByEmail}</div>
																			)}
																		</div>
																	</div>
																</td>
																<td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
																	{new Date(file.uploadedAt).toLocaleDateString()}
																</td>
																<td className="px-6 py-4 whitespace-nowrap text-sm">
																	<div className="flex items-center space-x-2">
																		<button
																			onClick={() => openPreview(file)}
																			className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
																			title="Preview file"
																		>
																			<svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																				<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
																				<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
																			</svg>
																			Preview
																		</button>
																		<button
																			onClick={() => openPermissions(file)}
																			className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
																			title="Manage permissions"
																		>
																			Manage Permissions
																		</button>
																		<button
																			onClick={() => handleDownloadFile(file)}
																			className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
																			title="Download file"
																		>
																			<svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																				<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
																			</svg>
																			Download
																		</button>
																		{/* Trace Views Button */}
																		{(file.uploadedById === user?.id) && (
																			<button
																				onClick={() => {
																					setTraceFile({ id: file.id, name: file.originalName });
																					setShowTraceModal(true);
																				}}
																				className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-orange-500 hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-400"
																				title="Trace Views"
																			>
																				<svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																					<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
																				</svg>
																				Trace Views
																			</button>
																		)}
																		{/* Delete button - only for owner */}
																		{(file.uploadedById === user?.id) && (
																			<button
																				onClick={() => deleteFile(file)}
																				className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
																				title="Delete file"
																			>
																				<svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																					<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
																				</svg>
																				Delete
																			</button>
																		)}
																	</div>
																</td>
															</tr>
														))}
													{files.filter(file => fileTab === 'sent' ? file.uploadedById === user?.id : file.uploadedById !== user?.id).length === 0 && (
														<tr>
															<td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500">
																No {fileTab === 'sent' ? 'sent' : 'received'} files found.
															</td>
														</tr>
													)}
												</tbody>
											</table>
										</div>
									</div>
								</div>
							) : activeTab === 'investors' ? (
								<InvestorsManagement />
							) : activeTab === 'startups' ? (() => {
								const startupsTotalPages = Math.ceil(startups.length / startupsPerPage);
								const startupsStartIndex = (startupsCurrentPage - 1) * startupsPerPage;
								const startupsEndIndex = startupsStartIndex + startupsPerPage;
								const currentStartups = startups.slice(startupsStartIndex, startupsEndIndex);

								return (
									<div className="p-6 flex flex-col h-[calc(100vh-140px)]">
										<div className="flex justify-between items-center mb-6">
											<h2 className="text-xl font-semibold text-gray-900">Startups</h2>
											<button
												onClick={() => setShowCreateStartupModal(true)}
												className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center space-x-2"
											>
												<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
												</svg>
												<span>Create Startup</span>
											</button>
										</div>

										<div className="flex-1 min-h-0 overflow-y-auto shadow ring-1 ring-black ring-opacity-5 md:rounded-lg mb-6">
											<table className="min-w-full divide-y divide-gray-300">
												<thead className="bg-gray-50 sticky top-0 z-10">
													<tr>
														<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
															Startup
														</th>
														<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
															Industry
														</th>
														<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
															Category
														</th>
														<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
															Stage
														</th>
														<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
															Address
														</th>
														<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
															Status
														</th>
														<th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
															Actions
														</th>
													</tr>
												</thead>
												<tbody className="bg-white divide-y divide-gray-200">
													{currentStartups.map((startup) => (
														<tr
															key={startup.id}
															onClick={() => navigate(`/${tenantId}/startups/${startup.id}`)}
															className="hover:bg-gray-50 cursor-pointer"
														>
															<td className="px-4 py-3 whitespace-nowrap">
																<div className="flex items-center">
																	<div className="flex-shrink-0 h-10 w-10">
																		<div className="h-10 w-10 rounded-full bg-gradient-to-r from-green-500 to-green-600 flex items-center justify-center">
																			<span className="text-white font-medium">
																				{(startup.organizationName || startup.name)?.charAt(0)?.toUpperCase()}
																			</span>
																		</div>
																	</div>
																	<div className="ml-4">
																		<div className="text-sm font-medium text-gray-900">{startup.organizationName || 'Not specified'}</div>
																		<div className="text-sm text-gray-500">{startup.name}</div>
																	</div>
																</div>
															</td>

															<td className="px-4 py-3 whitespace-nowrap">
																<div className="text-sm text-gray-900">{startup.industry || 'Not specified'}</div>
															</td>
															<td className="px-4 py-3 whitespace-nowrap">
																<div className="text-sm text-gray-900">{startup.organizationType || 'Not specified'}</div>
															</td>
															<td className="px-4 py-3 whitespace-nowrap">
																<div className="text-sm text-gray-900">{startup.stage || 'Not specified'}</div>
															</td>
															<td className="px-4 py-3 whitespace-nowrap">
																<div className="text-sm text-gray-900">{startup.address || 'Not specified'}</div>
															</td>
															<td className="px-4 py-3 whitespace-nowrap">
																<span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
																	Active
																</span>
															</td>
															<td className="px-4 py-3 whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
																{startup.role !== 'ADMIN' && startup.role !== 'VIEWER' && (
																	<button
																		type="button"
																		onClick={() =>
																			handleResetPassword({
																				id: startup.id,
																				name: startup.organizationName || startup.name,
																				email: startup.email
																			})
																		}
																		className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50 inline-flex"
																		title="Reset password"
																	>
																		<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																			<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
																		</svg>
																	</button>
																)}
															</td>
														</tr>
													))}
												</tbody>
											</table>
										</div>

										{/* Fixed Bottom - Pagination */}
										<div className="bg-white border-t border-gray-200 py-2 px-4 rounded-lg shadow">
											<div className="flex items-center justify-between">
												<div className="text-sm text-gray-700">
													Showing <span className="font-medium">{startupsStartIndex + 1}</span> to <span className="font-medium">{Math.min(startupsEndIndex, startups.length)}</span> of <span className="font-medium">{startups.length}</span> startups
												</div>
												{startupsTotalPages > 1 && (
													<div className="flex items-center space-x-2">
														<button
															onClick={() => setStartupsCurrentPage(startupsCurrentPage - 1)}
															disabled={startupsCurrentPage === 1}
															className="px-3 py-1 rounded-md border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
														>
															Previous
														</button>
														<div className="flex items-center space-x-1">
															{[...Array(startupsTotalPages)].map((_, index) => {
																const page = index + 1;
																if (
																	page === 1 ||
																	page === startupsTotalPages ||
																	(page >= startupsCurrentPage - 1 && page <= startupsCurrentPage + 1)
																) {
																	return (
																		<button
																			key={page}
																			onClick={() => setStartupsCurrentPage(page)}
																			className={`px-3 py-1 rounded-md text-sm font-medium ${startupsCurrentPage === page
																				? 'bg-blue-600 text-white'
																				: 'border border-gray-300 text-gray-700 hover:bg-gray-50'
																				}`}
																		>
																			{page}
																		</button>
																	);
																} else if (page === startupsCurrentPage - 2 || page === startupsCurrentPage + 2) {
																	return <span key={page} className="px-2 text-gray-500">...</span>;
																}
																return null;
															})}
														</div>
														<button
															onClick={() => setStartupsCurrentPage(startupsCurrentPage + 1)}
															disabled={startupsCurrentPage === startupsTotalPages}
															className="px-3 py-1 rounded-md border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
														>
															Next
														</button>
													</div>
												)}
											</div>
										</div>
									</div>
								);
							})() : activeTab === 'users' ? (
								<div className="p-6 space-y-6">
									<div className="flex justify-between items-center mb-6">
										<div>
											<h2 className="text-xl font-semibold text-gray-900">User Management</h2>
											<p className="text-sm text-gray-500 mt-1">Manage all user accounts and permissions</p>
										</div>
										<CreateUserForm onCreated={loadData} />
									</div>

									<div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
										<table className="min-w-full divide-y divide-gray-300">
											<thead className="bg-gray-50">
												<tr>
													<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
														User
													</th>
													<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
														Role
													</th>
													<th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
														Status
													</th>
													<th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
														Actions
													</th>
												</tr>
											</thead>
											<tbody className="bg-white divide-y divide-gray-200">
												{users.map((u) => (
													<tr key={u.id} className="hover:bg-gray-50">
														<td className="px-4 py-3 whitespace-nowrap">
															<div className="flex items-center">
																<div className="flex-shrink-0 h-10 w-10">
																	<div className="h-10 w-10 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 flex items-center justify-center">
																		<span className="text-white font-medium">
																			{u.name?.charAt(0)?.toUpperCase()}
																		</span>
																	</div>
																</div>
																<div className="ml-4">
																	<div className="text-sm font-medium text-gray-900">{u.name}</div>
																	<div className="text-sm text-gray-500">{u.email}</div>
																</div>
															</div>
														</td>
														<td className="px-4 py-3 whitespace-nowrap">
															<span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${u.role === 'ADMIN' ? 'bg-purple-100 text-purple-800' :
																u.role === 'ENTREPRENEUR' ? 'bg-green-100 text-green-800' :
																	'bg-blue-100 text-blue-800'
																}`}>
																{u.role}
															</span>
														</td>
														<td className="px-6 py-4 whitespace-nowrap">
															<span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
																Active
															</span>
														</td>
														<td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
															<div className="flex items-center justify-end space-x-2">
																{/* Password Reset - Show for all roles except ADMIN and VIEWER */}
																{u.role !== 'ADMIN' && u.role !== 'VIEWER' && (
																	<button
																		onClick={(e) => {
																			e.stopPropagation();
																			handleResetPassword(u);
																		}}
																		className="text-blue-600 hover:text-blue-900 px-3 py-1 rounded hover:bg-blue-50 transition"
																		title="Reset Password"
																	>
																		<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																			<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
																		</svg>
																	</button>
																)}
																{/* Delete User - Hide for ADMIN users */}
																{u.role !== 'ADMIN' && (
																	<button
																		onClick={(e) => {
																			e.stopPropagation();
																			handleDeleteUser(u);
																		}}
																		className="text-red-600 hover:text-red-900 px-3 py-1 rounded hover:bg-red-50 transition"
																		title="Delete User"
																	>
																		<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																			<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
																		</svg>
																	</button>
																)}
															</div>
														</td>
													</tr>
												))}
											</tbody>
										</table>
									</div>
								</div>
							) : activeTab === 'reports' ? (
								<div className="p-6">
									{reportsLoading ? (
										<div className="flex items-center justify-center py-16">
											<div className="text-center">
												<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
												<p className="mt-4 text-gray-600">Loading reports...</p>
											</div>
										</div>
									) : (
										<div className="space-y-6">
											{/* Reports Tabs */}
											<div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
												<div className="border-b border-gray-200">
													<nav className="flex -mb-px">
														{reportsTabs.map(tab => (
															<button
																key={tab.id}
																onClick={() => setReportsActiveTab(tab.id)}
																className={`px-6 py-4 text-sm font-medium border-b-2 transition ${reportsActiveTab === tab.id
																	? 'border-blue-600 text-blue-600'
																	: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
																	}`}
															>
																{tab.label}
															</button>
														))}
													</nav>
												</div>
											</div>

											{/* Reports Content */}
											<div>
												{reportsActiveTab === 'dashboard' && renderReportsDashboardTab()}
												{reportsActiveTab === 'investments' && renderReportsInvestmentsTab()}
												{reportsActiveTab === 'pitch-events' && renderReportsPitchEventsTab()}
												{reportsActiveTab === 'user-activity' && renderReportsUserActivityTab()}
											</div>
										</div>
									)}
								</div>
							) : activeTab === 'events' ? (
								<PitchEventList embedded={true} />
							) : activeTab === 'settings' ? (
								<SettingsPage />
							) : activeTab === 'account' ? (
								<div className="p-6 space-y-6">
									{/* Profile Information */}
									<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
										<h2 className="text-xl font-semibold text-gray-900 mb-4">Profile Information</h2>
										<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
											<div className="flex items-center space-x-4">
												<div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full flex items-center justify-center">
													<span className="text-white font-semibold text-xl">
														{user?.name?.charAt(0)?.toUpperCase()}
													</span>
												</div>
												<div>
													<p className="text-lg font-semibold text-gray-900">{user?.name}</p>
													<p className="text-sm text-gray-500">{user?.email}</p>
													<p className="text-xs text-gray-400 mt-1">Administrator</p>
												</div>
											</div>
											<div className="space-y-3">
												<div>
													<p className="text-sm font-medium text-gray-600">Role</p>
													<p className="text-sm text-gray-900">Administrator</p>
												</div>
												<div>
													<p className="text-sm font-medium text-gray-600">Account Status</p>
													<span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
														Active
													</span>
												</div>
											</div>
										</div>
									</div>

									{/* Usage Statistics */}
									<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
										{/* Files Usage */}
										<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
											<h3 className="text-lg font-semibold text-gray-900 mb-4">Files Usage</h3>
											<div className="space-y-4">
												<div>
													<div className="flex justify-between items-center mb-2">
														<span className="text-sm font-medium text-gray-600">Total Files</span>
														<span className="text-lg font-bold text-gray-900">{files.length}</span>
													</div>
													<div className="w-full bg-gray-200 rounded-full h-2">
														<div
															className="bg-blue-600 h-2 rounded-full"
															style={{ width: `${Math.min((files.length / 1000) * 100, 100)}%` }}
														></div>
													</div>
													<p className="text-xs text-gray-500 mt-1">Limit: 1,000 files</p>
												</div>
												<div className="pt-4 border-t border-gray-200">
													<div className="flex justify-between text-sm">
														<span className="text-gray-600">Files by Type</span>
													</div>
													<div className="mt-2 space-y-2">
														{['PDF', 'Images', 'Documents', 'Other'].map((type) => (
															<div key={type} className="flex justify-between text-xs">
																<span className="text-gray-500">{type}</span>
																<span className="text-gray-900">
																	{Math.floor(files.length / 4)}
																</span>
															</div>
														))}
													</div>
												</div>
											</div>
										</div>

										{/* Storage Usage */}
										<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
											<h3 className="text-lg font-semibold text-gray-900 mb-4">Storage Usage</h3>
											<div className="space-y-4">
												<div>
													<div className="flex justify-between items-center mb-2">
														<span className="text-sm font-medium text-gray-600">Used Storage</span>
														<span className="text-lg font-bold text-gray-900">
															{(files.reduce((sum, f) => sum + (f.fileSize || 0), 0) / 1024 / 1024 / 1024).toFixed(2)} GB
														</span>
													</div>
													<div className="w-full bg-gray-200 rounded-full h-2">
														<div
															className="bg-green-600 h-2 rounded-full"
															style={{
																width: `${Math.min(
																	(files.reduce((sum, f) => sum + (f.fileSize || 0), 0) / 1024 / 1024 / 1024 / 100) * 100,
																	100
																)}%`
															}}
														></div>
													</div>
													<p className="text-xs text-gray-500 mt-1">Limit: 100 GB</p>
												</div>
												<div className="pt-4 border-t border-gray-200">
													<div className="flex justify-between text-sm mb-2">
														<span className="text-gray-600">Available</span>
														<span className="text-gray-900">
															{Math.max(
																0,
																100 - (files.reduce((sum, f) => sum + (f.fileSize || 0), 0) / 1024 / 1024 / 1024)
															).toFixed(2)} GB
														</span>
													</div>
													<div className="flex justify-between text-sm">
														<span className="text-gray-600">Total</span>
														<span className="text-gray-900">100 GB</span>
													</div>
												</div>
											</div>
										</div>
									</div>

									{/* Plan Details */}
									<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
										<h3 className="text-lg font-semibold text-gray-900 mb-4">Plan Details</h3>
										<div className="grid grid-cols-1 md:grid-cols-3 gap-6">
											<div className="border border-gray-200 rounded-lg p-4">
												<div className="flex items-center justify-between mb-2">
													<span className="text-sm font-medium text-gray-600">Current Plan</span>
													<span className="px-3 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
														Professional
													</span>
												</div>
												<p className="text-2xl font-bold text-gray-900 mt-2">$99/month</p>
												<p className="text-xs text-gray-500 mt-1">Billed annually</p>
											</div>
											<div className="border border-gray-200 rounded-lg p-4">
												<div className="flex items-center justify-between mb-2">
													<span className="text-sm font-medium text-gray-600">Renewal Date</span>
												</div>
												<p className="text-lg font-semibold text-gray-900 mt-2">
													{new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString()}
												</p>
												<p className="text-xs text-gray-500 mt-1">30 days remaining</p>
											</div>
											<div className="border border-gray-200 rounded-lg p-4">
												<div className="flex items-center justify-between mb-2">
													<span className="text-sm font-medium text-gray-600">Features</span>
												</div>
												<ul className="text-sm text-gray-600 mt-2 space-y-1">
													<li className="flex items-center">
														<svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
															<path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
														</svg>
														Unlimited Users
													</li>
													<li className="flex items-center">
														<svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
															<path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
														</svg>
														100 GB Storage
													</li>
													<li className="flex items-center">
														<svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
															<path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
														</svg>
														Priority Support
													</li>
												</ul>
											</div>
										</div>
									</div>

									{/* Activity Logs */}
									<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
										<h3 className="text-lg font-semibold text-gray-900 mb-4">Activity Logs</h3>
										<div className="space-y-3">
											{[1, 2, 3, 4, 5].map((i) => (
												<div key={i} className="flex items-start space-x-3 p-3 border border-gray-100 rounded-lg hover:bg-gray-50">
													<div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
														<svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
															<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
														</svg>
													</div>
													<div className="flex-1 min-w-0">
														<p className="text-sm text-gray-900">
															<span className="font-medium">You</span> {i === 1 && 'uploaded a new file'}
															{i === 2 && 'updated file permissions'}
															{i === 3 && 'created a new user'}
															{i === 4 && 'viewed file preview'}
															{i === 5 && 'logged in'}
														</p>
														<p className="text-xs text-gray-500 mt-1">
															{new Date(Date.now() - i * 60 * 60 * 1000).toLocaleString()}
														</p>
													</div>
												</div>
											))}
										</div>
									</div>
								</div>
							) : null}
						</div>
					</>
				)}
			</div>


			{/* File Trace Modal */}
			<FileTraceModal
				isOpen={showTraceModal}
				onClose={() => {
					setShowTraceModal(false);
					setTraceFile(null);
				}}
				fileId={traceFile?.id}
				fileName={traceFile?.name}
			/>

			{/* File Preview Modal */}
			< FilePreviewModal
				file={previewFile}
				isOpen={showPreview}
				onClose={closePreview}
			/>

			{/* Create Startup Modal */}
			< CreateStartupModal
				isOpen={showCreateStartupModal}
				onClose={() => setShowCreateStartupModal(false)}
				onSuccess={loadData}
			/>

			{/* Alert Dialog */}
			< AlertDialog
				isOpen={alert.isOpen}
				onClose={() => setAlert({ ...alert, isOpen: false })}
				title={alert.title}
				message={alert.message}
				type={alert.type}
			/>

			{/* Confirm Dialog */}
			< ConfirmDialog
				isOpen={confirm.isOpen}
				onClose={() => setConfirm({ ...confirm, isOpen: false })}
				onConfirm={confirm.onConfirm || (() => { })}
				title={confirm.title}
				message={confirm.message}
				type={confirm.type}
			/>
		</>
	);
};

export default AdminDashboard;
